import Color from 'color';
import * as d3 from 'd3';
import * as d3BboxCollide from 'd3-bboxCollide';
import * as CSSTransformParser from 'transform-parser';

import {
  formatStringAsDate,
  MONTH_DAY_OF_MONTH_YEAR_FORMAT,
} from '@/lib/datetime';
import {
  breakString,
  isBlank,
  isFunction,
  isNullOrFalse,
} from 'shared/lib/utils';

import {
  floatGreaterThanOrEqual,
  floatLessThan,
  floatLessThanOrEqual,
} from '@/lib/math';

import {
  ANNOTATION_INDEX_FONT_SIZE,
  ANNOTATION_INDEX_TEXT_Y_OFFSET_MULTIPLIER,
  ANNOTATION_TIMESTAMP_FONT_SIZE,
  ANNOTATION_TIMESTAMP_VERTICAL_DISTANCE_TO_ANNOTATION_BOX,
  DEFAULT_ANNOTATION_FONT_SIZE,
} from '@/lib/config';
import { setUpDragableElements } from './dragAndDrop';

import {
  ANNOTATION_POINTERS_LAYER_PREFIX,
  ANNOTATION_TEXTS_LAYER_PREFIX,
  annotationMarkLayerNamePrefix,
} from '@/lib/data/chart';

const ANNOTATION_RECT_STYLE_FILL_OPACITY = 0.5;

const NUMBER_OF_ANNOTATION_ROWS = 4;

// With a cell height of 4, the ideal distance is 2.
const IDEAL_CELL_Y_DISTANCE_TO_EVENT_MARK = 2;

// How much rotation available per quadrant.
const ARROW_AVAILABLE_QUADRANT_ROTATION_IN_DEGREES = 30;

// How much the link arrow will be close to the target.
const ARROW_DISTANCE_TO_POINT_COEFFICIENT = 0.75;

const ANNOTATION_LINK_STROKE_WIDTH = 1;
const ANNOTATION_LINK_COLORFUL_STROKE_COLOR_DARKEN = 0.25;
const ANNOTATION_LINK_NON_COLORFUL_STROKE_COLOR = '#666';
const ANNOTATION_POINTER_NON_COLORFUL_FILL_COLOR = '#666';
const ANNOTATION_POINTER_NON_COLORFUL_STROKE_COLOR = '#666';

function formatAnnotationTimestamp(timestamp) {
  return formatStringAsDate(timestamp, MONTH_DAY_OF_MONTH_YEAR_FORMAT);
}

// Return the 'cell x' for the given coordinate x.
function coordinateXToCellX(totalWidth, numberOfColumns, x) {
  const chartAnnotationColumnWidth = totalWidth / numberOfColumns;
  let upperBoundCheck;

  for (let i = 0; i < numberOfColumns; i += 1) {
    upperBoundCheck =
      i === numberOfColumns ? floatLessThan : floatLessThanOrEqual;

    if (
      floatGreaterThanOrEqual(x, i * chartAnnotationColumnWidth) &&
      upperBoundCheck(x, (i + 1) * chartAnnotationColumnWidth)
    ) {
      return i;
    }
  }

  console.error(
    `Unexpected "coordinateXToCellX" parameters: totalWidth=${totalWidth}, numberOfColumns=${numberOfColumns}, x=${x}`
  );

  return -1;
}

// Return the 'cell y' for the given coordinate y.
function coordinateYToCellY(totalHeight, numberOfRows, y) {
  const chartAnnotationRowHeight = totalHeight / numberOfRows;
  let upperBoundCheck;

  for (let i = 0; i < numberOfRows; i += 1) {
    upperBoundCheck = i === numberOfRows ? floatLessThan : floatLessThanOrEqual;

    if (
      floatGreaterThanOrEqual(y, i * chartAnnotationRowHeight) &&
      upperBoundCheck(y, (i + 1) * chartAnnotationRowHeight)
    ) {
      return i;
    }
  }

  console.error(
    `Unexpected "coordinateYToCellY" parameters: totalHeight=${totalHeight}, numberOfRows=${numberOfRows}, y=${y}`
  );

  return -1;
}

function getRecommendedAnnotationCellY(currentEventMarkCellY) {
  // First try y-placement with ideal distance. With a cell height of 4, the
  // ideal distance is 2.
  if (currentEventMarkCellY > Math.floor(NUMBER_OF_ANNOTATION_ROWS / 2 - 1)) {
    // The current recommendation is in the lower half.
    return currentEventMarkCellY - IDEAL_CELL_Y_DISTANCE_TO_EVENT_MARK;
  } else {
    // The current recommendation is in the upper half.
    return currentEventMarkCellY + IDEAL_CELL_Y_DISTANCE_TO_EVENT_MARK;
  }
}

function calculateRecommendedYCoordinate(
  boundingBoxHeight,
  numberOfRows,
  recommendedAnnotationCellY
) {
  const chartAnnotationRowHeight = boundingBoxHeight / numberOfRows;

  return (
    recommendedAnnotationCellY * chartAnnotationRowHeight +
    chartAnnotationRowHeight / 2
  );
}

function paintLinksAndPointers(
  $annotationLinks,
  $annotationPointers,
  annotationDataById
) {
  $annotationLinks
    .attr('opacity', (annotation) => {
      return annotation.style.showLink === false ? 0 : 1;
    })
    .attr('stroke', (annotation) => {
      return annotation.style.colorfulBackground
        ? Color(annotation.style.color).darken(
            ANNOTATION_LINK_COLORFUL_STROKE_COLOR_DARKEN
          )
        : ANNOTATION_LINK_NON_COLORFUL_STROKE_COLOR;
    })
    .attr('stroke-width', ANNOTATION_LINK_STROKE_WIDTH);

  $annotationPointers
    .attr('fill', (d) => {
      // NOTE: need to get data from given annotationDataById even though it's
      // already there in the D3 node.
      const annotationId = d.datum.annotations[0].id;
      const annotationData = annotationDataById.get(annotationId);

      if (!annotationData) {
        console.warn(
          `Couldn't find annotation data ID=${d.annotationId} to paint annotation link`
        );
        return ANNOTATION_POINTER_NON_COLORFUL_FILL_COLOR;
      }

      return annotationData.style.colorfulBackground
        ? Color(annotationData.style.color).darken(
            ANNOTATION_LINK_COLORFUL_STROKE_COLOR_DARKEN
          )
        : ANNOTATION_POINTER_NON_COLORFUL_FILL_COLOR;
    })
    .attr('stroke', (d) => {
      // NOTE: need to get data from given annotationDataById even though it's
      // already there in the D3 node.
      const annotationId = d.datum.annotations[0].id;
      const annotationData = annotationDataById.get(annotationId);

      if (!annotationData) {
        console.warn(
          `Couldn't find annotation data ID=${d.annotationId} to paint annotation link`
        );
        return ANNOTATION_POINTER_NON_COLORFUL_FILL_COLOR;
      }

      return annotationData.style.colorfulBackground
        ? Color(annotationData.style.color).darken(
            ANNOTATION_LINK_COLORFUL_STROKE_COLOR_DARKEN
          )
        : ANNOTATION_POINTER_NON_COLORFUL_STROKE_COLOR;
    });
}

// TODO(CTX-163): try SVG transform binding for potential better performance.
function makePositionAnnotationLinksAndPointers(
  $annotationGroups,
  $annotationLinks,
  $annotationPointers,
  annotationBBoxesById,
  linkDataByAnnotationId
) {
  return () => {
    $annotationGroups.attr('transform', (node) => {
      const annotationId = node.datum.annotations[0].id;
      const bbox = annotationBBoxesById.get(annotationId);
      const { x, y } = bbox;
      return `translate(${x}, ${y})`;
    });

    $annotationLinks.attr('d', (annotation) => {
      const annotationId = annotation.id;
      const annotationFontSize = annotation.style.fontSize;
      const bbox = annotationBBoxesById.get(annotationId);
      const { x, y, width } = bbox;

      const linkData = linkDataByAnnotationId.get(annotationId);

      const sourceTextAlignXOffset =
        annotation.style.textAlign === 'center'
          ? 0
          : annotation.style.textAlign === 'left'
          ? width / 2 - annotation.style.padding.horizontal
          : annotation.style.textAlign === 'right'
          ? -1 * (width / 2) - annotation.style.padding.horizontal
          : 0;

      // NOTE: The font size should have been divided by 2? But 4 seems to
      // center the link source right in the middle of the annotation text.
      const sourceFontSizeYOffset = annotationFontSize / 4;

      const sourceTextLinesYOffset =
        ((annotation.derivedTitle.length - 1) * annotationFontSize) / 2;

      const source = [x, y];
      const annotationCenterSource = [
        x + sourceTextAlignXOffset,
        y - sourceFontSizeYOffset + sourceTextLinesYOffset,
      ];
      const target = linkData.target;

      // NOTE: `source - target` so that the angles reflect the diagram below.
      // Doesn't make a difference compared to `target - source`.
      const horizontalDistance = source[0] - target[0];
      const verticalDistance = source[1] - target[1];

      const angleInRadians = Math.atan2(verticalDistance, horizontalDistance);
      const angleInDegrees = angleInRadians * (180 / Math.PI);

      // +---------------+
      // |\      |      /|
      // | \  F  |  G  / |
      // |  \    |    /  |
      // |   \   |   /   |
      // | E  \  |  /  H |
      // |     \ | /     |
      // |______\|/______|
      // |      /|\      |
      // |     / | \     |
      // | D  /  |  \  A |
      // |   /   |   \   |
      // |  /    |    \  |
      // | /  C  |  B  \ |
      // |/      |      \|
      // +---------------+

      let linkFunction;

      if (angleInDegrees >= 0 && angleInDegrees < 45) {
        // A.
        linkFunction = d3.linkHorizontal;
      } else if (angleInDegrees >= 45 && angleInDegrees < 90) {
        // B.
        linkFunction = d3.linkVertical;
      } else if (angleInDegrees >= 90 && angleInDegrees < 135) {
        // C.
        linkFunction = d3.linkVertical;
      } else if (angleInDegrees >= 135 && angleInDegrees < 180) {
        // D.
        linkFunction = d3.linkHorizontal;
      } else if (angleInDegrees >= -180 && angleInDegrees < -135) {
        // E.
        linkFunction = d3.linkHorizontal;
      } else if (angleInDegrees >= -135 && angleInDegrees < -90) {
        // F.
        linkFunction = d3.linkVertical;
      } else if (angleInDegrees >= -90 && angleInDegrees < -45) {
        // G.
        linkFunction = d3.linkVertical;
      } else if (angleInDegrees >= -45 && angleInDegrees < 0) {
        // H.
        linkFunction = d3.linkHorizontal;
      } else {
        console.warn(`Invalid angle: ${angleInDegrees}`);
      }

      linkData.source = annotationCenterSource;

      return linkFunction()(linkData);
    });

    $annotationPointers.attr('transform', (pointerNode) => {
      const annotationId = pointerNode.datum.annotations[0].id;
      const annotationNode = annotationBBoxesById.get(annotationId);
      const { bounds } = pointerNode;
      const pointerHeight = bounds.y2 - bounds.y1;
      const pointerWidth = bounds.x2 - bounds.x1;

      // NOTE: `source - target` so that the angles reflect the diagram below.
      // Doesn't make a difference compared to `target - source`.
      const horizontalDistance = annotationNode.x - pointerNode.x;
      const verticalDistance = annotationNode.y - pointerNode.y;

      const angleInRadians = Math.atan2(verticalDistance, horizontalDistance);
      const angleInDegrees = angleInRadians * (180 / Math.PI);

      // +---------------+
      // |\      |      /|
      // | \  F  |  G  / |
      // |  \    |    /  |
      // |   \   |   /   |
      // | E  \  |  /  H |
      // |     \ | /     |
      // |______\|/______|
      // |      /|\      |
      // |     / | \     |
      // | D  /  |  \  A |
      // |   /   |   \   |
      // |  /    |    \  |
      // | /  C  |  B  \ |
      // |/      |      \|
      // +---------------+

      const r = ARROW_AVAILABLE_QUADRANT_ROTATION_IN_DEGREES;
      const d = ARROW_DISTANCE_TO_POINT_COEFFICIENT;

      let rotationInDegrees;
      let x;
      let y;

      if (angleInDegrees >= 0 && angleInDegrees < 45) {
        // A.
        rotationInDegrees = 90 + r * Math.sin(angleInRadians);
        x = pointerNode.x + pointerWidth * d;
        y = pointerNode.y;
      } else if (angleInDegrees >= 45 && angleInDegrees < 90) {
        // B.
        rotationInDegrees = 180 - r * Math.cos(angleInRadians);
        x = pointerNode.x;
        y = pointerNode.y + pointerHeight * d;
      } else if (angleInDegrees >= 90 && angleInDegrees < 135) {
        // C.
        rotationInDegrees = 180 - r * Math.cos(angleInRadians);
        x = pointerNode.x;
        y = pointerNode.y + pointerHeight * d;
      } else if (angleInDegrees >= 135 && angleInDegrees < 180) {
        // D.
        rotationInDegrees = 270 - r * Math.sin(angleInRadians);
        x = pointerNode.x - pointerWidth * d;
        y = pointerNode.y;
      } else if (angleInDegrees >= -180 && angleInDegrees < -135) {
        // E.
        rotationInDegrees = 270 - r * Math.sin(angleInRadians);
        x = pointerNode.x - pointerWidth * d;
        y = pointerNode.y;
      } else if (angleInDegrees >= -135 && angleInDegrees < -90) {
        // F.
        rotationInDegrees = 0 + r * Math.cos(angleInRadians);
        x = pointerNode.x;
        y = pointerNode.y - pointerHeight * d;
      } else if (angleInDegrees >= -90 && angleInDegrees < -45) {
        // G.
        rotationInDegrees = 0 + r * Math.cos(angleInRadians);
        x = pointerNode.x;
        y = pointerNode.y - pointerHeight * d;
      } else if (angleInDegrees >= -45 && angleInDegrees < 0) {
        // H.
        rotationInDegrees = 90 + r * Math.sin(angleInRadians);
        x = pointerNode.x + pointerWidth * d;
        y = pointerNode.y;
      } else {
        console.warn(`Invalid angle: ${angleInDegrees}`);
      }

      return `translate(${x}, ${y}) rotate(${rotationInDegrees})`;
    });
  };
}

type RecommendationsByAnnotationId = Map<
  string,
  { cellY: number[]; recommendedYCoordinate: number }
>;

function recommendAnnotationYPlacements(
  chartAreaBBox,
  annotationPointerCellsByAnnotationId,
  numberOfAnnotationColumns
): RecommendationsByAnnotationId {
  /** @type {DOMRect} */
  // Store in which cells in the cell matrix the event marks are in.
  const placementGrid = new Array(numberOfAnnotationColumns);

  for (let i = 0; i < numberOfAnnotationColumns; i += 1) {
    placementGrid[i] = new Array(NUMBER_OF_ANNOTATION_ROWS);

    for (let j = 0; j < NUMBER_OF_ANNOTATION_ROWS; j += 1) {
      placementGrid[i][j] = {
        x: i,
        y: j,
        nearbyAnnotations: [],
        placementTaints: [],
      };
    }
  }

  // Store event marks in their respective placement grid coordinates.
  annotationPointerCellsByAnnotationId.forEach(
    ([cellX, cellY], annotationId) => {
      // Add event marks to the left of the current column too.
      if (cellX > 0) {
        placementGrid[cellX - 1][cellY].nearbyAnnotations.push(annotationId);
      }

      placementGrid[cellX][cellY].nearbyAnnotations.push(annotationId);

      // Uncomment this to add event marks to the right of the current column too
      // if things get too busy around event marks?
      //
      // It would make it more difficult to place annotations, though.
      //
      // if (cellX + 1 < placementGrid.length) {
      //   placementGrid[cellX + 1][cellY].nearbyAnnotations.push(annotationPointerIdx);
      // }
    }
  );

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return Array.from(annotationPointerCellsByAnnotationId).reduce(
    (
      recommendationsByAnnotationId: RecommendationsByAnnotationId,
      [annotationId, [_annotationPointerCellX, annotationPointerCellY]],
      cellXIdx
    ) => {
      // Get the recommended cell y placement based on the event mark cell y.
      const recommendedAnnotationCellY = getRecommendedAnnotationCellY(
        annotationPointerCellY
      );

      // If this is the first annotation to be placed, just follow the first
      // recommendation.
      //
      // TODO: check if there are no event marks in the recommended cell.
      if (cellXIdx === 0) {
        // Signal to avoid placing the next annotation on the same row as the
        // current one.
        if (cellXIdx < placementGrid.length) {
          const foo = placementGrid[cellXIdx + 1];

          if (foo) {
            const bar = foo[recommendedAnnotationCellY];

            if (bar) {
              bar.placementTaints.push(annotationId);
            }
          }
        }

        return recommendationsByAnnotationId.set(annotationId, {
          cellY: recommendedAnnotationCellY,
          recommendedYCoordinate: calculateRecommendedYCoordinate(
            chartAreaBBox.height,
            NUMBER_OF_ANNOTATION_ROWS,
            recommendedAnnotationCellY
          ),
        });
      }

      // TODO: otherwise, check for the best placement column-by-column,
      // avoiding placement taints. First try current column, then continuously
      // expand right then left. Within columns, expand top and then bottom.

      // TODO: a possible improvement on the above would be to first expand
      // right if we're in the left half of the grid and vice-versa.
      //
      // TODO: and same idea for expanding within columns: expand down if in top
      // half and vice-versa.

      // Instead of the full logic above we do something simpler here, assuming
      // that a placement recommendation in the current column will be found:
      //
      // 1. get the untainted cells in the column currently recommended for
      //    placement.
      // 2. get their distances to the relative event mark.
      // 3. see if the cells at the perfect distance are untainted. If yes, pick
      //    the first.
      // 4. If not, pick the first found at growing distances.

      // Event marks are iterated from left to right, so their array index is
      // equivalent to which grid column their annotations should be placed at.
      const recommendedPlacementCellX = cellXIdx;

      // Default to the initial recommendation.
      let correctedAnnotationCellY = recommendedAnnotationCellY;

      const untaintedColumnCells = placementGrid[
        recommendedPlacementCellX
      ].filter(
        (cell) =>
          cell.placementTaints.length === 0 &&
          cell.nearbyAnnotations.length === 0
      );

      // A sparse mapping from 'cell y distance from event mark to annotation'
      // to an 'array of cells with that distance'.
      const untaintedCellsByDistance = untaintedColumnCells.reduce(
        (byDistance, cell) => {
          const distance = Math.abs(annotationPointerCellY - cell.y);

          if (Object.prototype.hasOwnProperty.call(byDistance, distance)) {
            byDistance[distance].push(cell);
            return byDistance;
          } else {
            const object = {};
            // There can be more than one here, for example, at a distance of
            // one, there could be one cell above and one below.
            object[distance] = [cell];
            return Object.assign(byDistance, object);
          }
        },
        {}
      );

      // Start trying with the ideal distance.
      const idealDistanceAttempt = IDEAL_CELL_Y_DISTANCE_TO_EVENT_MARK;

      // Try ideal distance, then something closer, then something further away.
      // Hopefully one of them works?
      const distanceAttempts = [
        idealDistanceAttempt,
        idealDistanceAttempt - 1,
        idealDistanceAttempt + 1,
      ];

      for (let j = 0; j < distanceAttempts.length; j += 1) {
        const untaintedCellsAtDistance =
          untaintedCellsByDistance[distanceAttempts[j]];

        if (untaintedCellsAtDistance) {
          let found = false;

          for (let k = 0; k < untaintedCellsAtDistance.length; k += 1) {
            const untaintedCellAtDistance = untaintedCellsAtDistance[k];

            // If found one, set it to be the corrected one and stop searching.
            if (untaintedCellAtDistance) {
              correctedAnnotationCellY = untaintedCellAtDistance.y;
              found = true;
              break;
            }
          }

          if (found) {
            break;
          }
        }
      }

      // Signal to avoid placing the next annotation on the same row as the
      // current one.
      if (cellXIdx + 1 < placementGrid.length) {
        placementGrid[cellXIdx + 1][
          correctedAnnotationCellY
        ].placementTaints.push(annotationId);
      }

      return recommendationsByAnnotationId.set(annotationId, {
        cellY: correctedAnnotationCellY,
        recommendedYCoordinate: calculateRecommendedYCoordinate(
          chartAreaBBox.height,
          NUMBER_OF_ANNOTATION_ROWS,
          correctedAnnotationCellY
        ),
      });
    },
    new Map()
  );
}

function breakAnnotationTextIntoMultipleLines(maximumAnnotationLength, text) {
  if (isBlank(text)) {
    return '';
  }

  const lines = breakString(text, maximumAnnotationLength);
  const tspans = new Array(lines.length);

  for (let i = 0; i < lines.length; i += 1) {
    const dyProp = i === 0 ? '' : "dy='1em'";
    tspans.push(`<tspan x="0" ${dyProp}>${lines[i]}</tspan>`);
  }

  return tspans.join('');
}

function createAnnotationGroupFromText(textEl) {
  const annotationData = textEl.__data__.datum.annotations[0];

  const groupEl = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  groupEl.setAttribute('id', `annotation-${annotationData.id}`);
  groupEl.classList.add('annotation');
  groupEl.classList.add('outline-none');

  textEl.classList.add('annotation-title');
  textEl.parentNode.appendChild(groupEl);

  // Getting the data set by Vega-lite and setting it on the parent too. ///////
  //
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  groupEl.__data__ = textEl.__data__;

  // NOTE: transferring the <text> transform to its <g> parent.
  groupEl.setAttribute('transform', textEl.getAttribute('transform'));
  textEl.removeAttribute('transform');

  // Create main background SVG rect. //////////////////////////////////////////

  const backgroundRectEl = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'rect'
  );
  backgroundRectEl.classList.add('background');

  // Create main foreground SVG rect for events (e.g., hover). /////////////////

  const foregroundRectEl = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'rect'
  );
  foregroundRectEl.classList.add('foreground');
  // Make hover events work.
  foregroundRectEl.classList.add('[pointer-events:all]');
  foregroundRectEl.setAttribute('fill', 'none');

  // Create annotation index SVG background. ///////////////////////////////////

  const orderIndicatorBackgroundCircleEl = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'circle'
  );
  orderIndicatorBackgroundCircleEl.classList.add('index-background');

  // Create annotation index SVG element. //////////////////////////////////////

  const orderIndicatorTextEl = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'text'
  );
  orderIndicatorTextEl.classList.add('index');

  // Create timestamp SVG element. /////////////////////////////////////////////

  const timestampTextEl = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'text'
  );
  timestampTextEl.classList.add('timestamp');

  // Add all elements to the main <g>. ////////////////////////////////////////

  groupEl.appendChild(timestampTextEl);
  groupEl.appendChild(backgroundRectEl);
  groupEl.appendChild(orderIndicatorBackgroundCircleEl);
  groupEl.appendChild(textEl);
  groupEl.appendChild(orderIndicatorTextEl);
  // NOTE: foregroundRectEl is last so it's on top.
  groupEl.appendChild(foregroundRectEl);

  return {
    groupEl,
    backgroundRectEl,
    foregroundRectEl,
    orderIndicatorBackgroundCircleEl,
    orderIndicatorTextEl,
    timestampTextEl,
  };
}

function renderAnnotation(
  storyId,
  annotationSetStyle: {
    showOrderIndicators?: boolean;
    fontSize?: number;
  },
  annotationData: {
    date?: string;
    style: {
      color?: string;
      colorfulBackground?: boolean;
      fontSize?: number;
      length?: number;
      padding: {
        horizontal: number;
        vertical: number;
      };
      showTimestamp?: boolean;
      textAlign?: string;
    };
  },
  annotationIdx,
  groupEl,
  textEl,
  backgroundRectEl,
  foregroundRectEl,
  orderIndicatorBackgroundCircleEl,
  orderIndicatorTextEl,
  timestampTextEl
) {
  const annotationFontSize =
    annotationData.style.fontSize || DEFAULT_ANNOTATION_FONT_SIZE;

  const annotationOrderIndicatorFontSize = annotationFontSize
    ? annotationFontSize * 1.1
    : ANNOTATION_INDEX_FONT_SIZE;

  const annotationTimestampFontSize = annotationFontSize
    ? annotationFontSize * 0.9
    : ANNOTATION_TIMESTAMP_FONT_SIZE;

  const annotationIndexBackgroundRadius =
    annotationOrderIndicatorFontSize * 0.8;

  // NOTE: this doesn't work for a <text> element with <tspan>s inside.
  // textEl.setAttribute('alignment-baseline', 'middle');

  textEl.setAttribute('font-size', annotationFontSize);

  const textElBBox = textEl.getBBox();
  const { x, y, width, height } = textElBBox;

  const padding = annotationData.style.padding;

  const backgroundX = x - padding.horizontal;
  const backgroundY = y - padding.vertical;
  const backgroundWidth = width + 2 * padding.horizontal;
  const backgroundHeight = height + 2 * padding.vertical;

  backgroundRectEl.setAttribute('x', backgroundX);
  backgroundRectEl.setAttribute('y', backgroundY);
  backgroundRectEl.setAttribute('width', backgroundWidth);
  backgroundRectEl.setAttribute('height', backgroundHeight);
  backgroundRectEl.setAttribute('filter', `url(#blur-filter-${storyId})`);

  foregroundRectEl.setAttribute('x', backgroundX);
  foregroundRectEl.setAttribute('y', backgroundY);
  foregroundRectEl.setAttribute('width', backgroundWidth);
  foregroundRectEl.setAttribute('height', backgroundHeight);

  if (annotationData.style.colorfulBackground) {
    backgroundRectEl.setAttribute('fill', annotationData.style.color);
    backgroundRectEl.setAttribute(
      'fill-opacity',
      ANNOTATION_RECT_STYLE_FILL_OPACITY
    );
  } else {
    backgroundRectEl.setAttribute('fill', '#ffffff');
    backgroundRectEl.setAttribute('fill-opacity', '0.8');
  }

  if (annotationSetStyle.showOrderIndicators) {
    orderIndicatorBackgroundCircleEl.setAttribute('cx', backgroundX);
    orderIndicatorBackgroundCircleEl.setAttribute('cy', backgroundY);
    orderIndicatorBackgroundCircleEl.setAttribute(
      'r',
      annotationIndexBackgroundRadius
    );
    orderIndicatorBackgroundCircleEl.setAttribute('fill-opacity', 1);

    if (annotationData.style.colorfulBackground) {
      orderIndicatorBackgroundCircleEl.setAttribute(
        'fill',
        annotationData.style.color
      );
    } else {
      orderIndicatorBackgroundCircleEl.setAttribute('fill', '#ededed');
      orderIndicatorBackgroundCircleEl.setAttribute('stroke', '#ccc');
    }
  } else {
    orderIndicatorBackgroundCircleEl.setAttribute('fill-opacity', 0);
  }

  orderIndicatorTextEl.textContent = annotationIdx;

  if (annotationSetStyle.showOrderIndicators) {
    orderIndicatorTextEl.setAttribute('x', backgroundX);
    orderIndicatorTextEl.setAttribute(
      'y',
      backgroundY +
        annotationOrderIndicatorFontSize *
          ANNOTATION_INDEX_TEXT_Y_OFFSET_MULTIPLIER
    );
    orderIndicatorTextEl.setAttribute(
      'font-size',
      annotationOrderIndicatorFontSize
    );
    orderIndicatorTextEl.setAttribute('font-weight', 'bold');
    orderIndicatorTextEl.setAttribute('text-anchor', 'middle');

    if (annotationData.style.colorfulBackground) {
      orderIndicatorTextEl.setAttribute('fill', '#ffffff');
    } else {
      orderIndicatorTextEl.setAttribute('fill', '#111111');
    }
  } else {
    orderIndicatorTextEl.setAttribute('fill', 'none');
  }

  if (annotationData.style.showTimestamp) {
    timestampTextEl.textContent = formatAnnotationTimestamp(
      annotationData.date
    );
    timestampTextEl.setAttribute('font-size', annotationTimestampFontSize);
    timestampTextEl.setAttribute('font-weight', 'bold');

    const timestampBackgroundX = backgroundX + backgroundWidth / 2;
    const timestampBackgroundY =
      backgroundY -
      ANNOTATION_TIMESTAMP_VERTICAL_DISTANCE_TO_ANNOTATION_BOX *
        (annotationData.style.colorfulBackground ? 2 : 1);

    timestampTextEl.setAttribute('x', timestampBackgroundX);
    timestampTextEl.setAttribute('y', timestampBackgroundY);
    timestampTextEl.setAttribute('fill', 'black');
    timestampTextEl.setAttribute('text-anchor', 'middle');
  } else {
    timestampTextEl.setAttribute('fill', 'none');
  }
}

function buildLinks(
  annotationPointerCoordinatesByAnnotationId,
  annotationDataById
) {
  const annotationLinksGroupEl = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'g'
  );
  annotationLinksGroupEl.setAttribute('class', 'annotation-links');

  // Create initial links. Their source and target are the same.
  const linkDataByAnnotationId: Map<
    string,
    { source: number[]; target: number[] }
  > = new Map(
    Array.from(
      annotationPointerCoordinatesByAnnotationId,
      ([annotationId, coordinates]) => [
        annotationId,
        {
          source: coordinates,
          target: coordinates,
        },
      ]
    )
  );

  for (const [annotationId, linkData] of linkDataByAnnotationId) {
    d3.select(annotationLinksGroupEl)
      .append('path')
      .attr('id', `annotation-link-${annotationId}`)
      .attr('d', d3.linkVertical()(linkData))
      .datum((_d) => annotationDataById.get(annotationId));
  }

  return {
    linkDataByAnnotationId,
    annotationLinksGroupEl,
    $annotationLinks: d3
      // NOTE: this element needs to be the same as the selector above.
      .select(annotationLinksGroupEl)
      .selectAll('path'),
  };
}

export default class AnnotationsRenderer {
  $annotationPointersGroups;
  $annotationPointerTargetsGroups;
  $annotationTextMarksGroups;

  $annotationLinks;
  $annotationPointers;
  $annotationTexts;
  annotationPointersCoordinates;

  chartAreaBBox;
  positionAnnotationLinksAndPointers;
  story;
  svgWrapperEl;

  annotationBBoxesById;
  annotationDataById;
  annotationGroupElsById;
  annotationPointerCoordinatesByAnnotationId;

  onAnnotationDragStart;
  onAnnotationDragEnd;

  _svgEl;

  render({
    el,
    story,
    shouldSetUpDraggableAnnotations,
    onAnnotationDragStart,
    onAnnotationDragEnd,
    setAnnotationGroupElsById,
    updateAnnotationByIdAndAttributes,
  }) {
    this.svgWrapperEl = el;
    this.story = story;
    this.onAnnotationDragStart = onAnnotationDragStart;
    this.onAnnotationDragEnd = onAnnotationDragEnd;

    this._svgEl = this.svgWrapperEl.querySelector('svg');

    // FIXME: is there a better way to get the bounding box for only the chart
    // area?
    this.chartAreaBBox = this._svgEl
      .querySelectorAll('.background')[0]
      .getBBox();

    if (this.chartAreaBBox.height === 0 || this.chartAreaBBox.width === 0) {
      console.error(
        `this.chartAreaBBox width or height equals zero: x=${this.chartAreaBBox.x}, y=${this.chartAreaBBox.y}, width=${this.chartAreaBBox.width}, height=${this.chartAreaBBox.height}`
      );
      return;
    }

    // ///////////////////////////////////////////////////////////////////////////

    const existingBlurFilter = this._svgEl.querySelector(
      `filter[id="blur-filter-${this.story.get('id')}"]`
    );

    if (!existingBlurFilter) {
      const blurFilter = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'filter'
      );
      blurFilter.setAttribute('id', `blur-filter-${this.story.get('id')}`);

      //////////////////////////////////////////////////////////////////////////

      const backgroundFeGaussianBlur = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'feGaussianBlur'
      );
      backgroundFeGaussianBlur.setAttribute('id', 'background');
      backgroundFeGaussianBlur.setAttribute('edgeMode', 'wrap');
      backgroundFeGaussianBlur.setAttribute('result', 'blurred');
      backgroundFeGaussianBlur.setAttribute('in', 'SourceGraphic');
      // TODO: make this configurable.
      backgroundFeGaussianBlur.setAttribute('stdDeviation', '3');

      blurFilter.appendChild(backgroundFeGaussianBlur);

      //////////////////////////////////////////////////////////////////////////

      const blurFeMerge = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'feMerge'
      );

      const backgroundFeGaussianBlurMergeNode = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'feMergeNode'
      );
      backgroundFeGaussianBlurMergeNode.setAttribute('in', 'blurred');

      blurFeMerge.appendChild(backgroundFeGaussianBlurMergeNode);

      blurFilter.appendChild(blurFeMerge);

      //////////////////////////////////////////////////////////////////////////

      this._svgEl.appendChild(blurFilter);
    }

    // Selecting annotation-related <g> elements rendered by Vega lite /////////

    this.$annotationPointersGroups = d3
      .select(this._svgEl)
      .selectAll(
        `g[class*='${annotationMarkLayerNamePrefix(
          ANNOTATION_POINTERS_LAYER_PREFIX
        )}']`
      );

    this.$annotationPointerTargetsGroups = d3
      .select(this._svgEl)
      .selectAll(
        `g[class*='${annotationMarkLayerNamePrefix(
          ANNOTATION_TEXTS_LAYER_PREFIX
        )}']`
      );

    this.$annotationTextMarksGroups = d3
      .select(this._svgEl)
      .selectAll(
        `g[class*='${annotationMarkLayerNamePrefix(
          ANNOTATION_TEXTS_LAYER_PREFIX
        )}']`
      );

    ////////////////////////////////////////////////////////////////////////////

    this.$annotationPointers = this.$annotationPointersGroups
      .selectAll('path')
      .attr('id', (d) => `annotation-pointer-${d.datum.annotations[0].id}`);

    this.$annotationTexts = this.$annotationTextMarksGroups.selectAll('text');

    ////////////////////////////////////////////////////////////////////////////

    // Parse `transform` string from SVG elements into usable coordinates.
    this.annotationPointerCoordinatesByAnnotationId = this.$annotationPointers
      .nodes()
      .reduce((memo, node) => {
        const annotationData = node.__data__.datum.annotations[0];
        return memo.set(
          annotationData.id,
          CSSTransformParser.parse(node.getAttribute('transform')).translate
        );
      }, new Map());

    this.annotationDataById = this.$annotationPointers
      .data()
      .reduce((memo, d, idx) => {
        const annotationData = d.datum.annotations[0];
        annotationData.idx = idx;
        return memo.set(annotationData.id, annotationData);
      }, new Map());

    // ///////////////////////////////////////////////////////////////////////////

    // ///////////////////////////////////////////////////////////////////////////

    this.annotationGroupElsById = this.$annotationTexts
      .nodes()
      .reduce((memo, textEl, idx) => {
        const annotationData = textEl.__data__.datum.annotations[0];

        const {
          groupEl,
          backgroundRectEl,
          foregroundRectEl,
          orderIndicatorBackgroundCircleEl,
          orderIndicatorTextEl,
          timestampTextEl,
        } = createAnnotationGroupFromText(textEl);

        renderAnnotation(
          this.story.get('id'),
          this.story.getIn(['annotationSet', 'style']).toJS(),
          annotationData,
          idx + 1,
          groupEl,
          textEl,
          backgroundRectEl,
          foregroundRectEl,
          orderIndicatorBackgroundCircleEl,
          orderIndicatorTextEl,
          timestampTextEl
        );

        return memo.set(annotationData.id, groupEl);
      }, new Map());

    // Create selectors.

    const $annotationGroups = d3.select(this._svgEl).selectAll('g.annotation');

    // Call callback to set the React component state with all SVG elements.
    setAnnotationGroupElsById(this.annotationGroupElsById);

    // Calculate and store initial bounding boxes for annotations.
    this.annotationBBoxesById = new Map(
      Array.from(this.annotationGroupElsById, ([annotationId, g]) => {
        const annotationData = g.__data__.datum.annotations[0];
        const userDefinedPosition = annotationData.style.position;

        let x;
        let y;

        const rect = g.querySelector('rect.background');

        const rectBBox = rect.getBBox();

        if (userDefinedPosition) {
          x = userDefinedPosition[0];
          y = userDefinedPosition[1];
        } else {
          // NOTE: this will be the position right on top of the text mark
          // created by Vega. Most certainly for a new annotation.
          const { translate } = CSSTransformParser.parse(
            g.getAttribute('transform')
          );
          x = translate[0];
          y = translate[1];
        }

        const bbox = {
          // NOTE: `annotationId` is used in the force simulation.
          annotationId,
          x: x,
          y: y,
          width: rectBBox.width,
          height: rectBBox.height,
        };

        return [annotationId, bbox];
      })
    );

    const parentMarkGroupElement =
      this.$annotationTextMarksGroups.node().parentNode;

    const { annotationLinksGroupEl, $annotationLinks, linkDataByAnnotationId } =
      buildLinks(
        this.annotationPointerCoordinatesByAnnotationId,
        this.annotationDataById
      );

    // Handle layer z-indices. /////////////////////////////////////////////////

    // NOTE: these can't be arrow functions because d3.each iterates the
    // underlying nodes via `this`.

    parentMarkGroupElement.append(annotationLinksGroupEl);
    parentMarkGroupElement.append(...this.$annotationPointersGroups.nodes());
    parentMarkGroupElement.append(
      ...this.$annotationPointerTargetsGroups.nodes()
    );
    parentMarkGroupElement.append(...this.$annotationTextMarksGroups.nodes());

    ////////////////////////////////////////////////////////////////////////////

    this.$annotationLinks = $annotationLinks;

    // Parse `transform` string from SVG elements into usable coordinates.
    this.annotationPointersCoordinates = this.$annotationPointers
      .nodes()
      .map(
        (node) =>
          CSSTransformParser.parse(node.getAttribute('transform')).translate
      );

    this.positionAnnotationLinksAndPointers =
      makePositionAnnotationLinksAndPointers(
        $annotationGroups,
        this.$annotationLinks,
        this.$annotationPointers,
        this.annotationBBoxesById,
        linkDataByAnnotationId
      );

    if (shouldSetUpDraggableAnnotations) {
      this.setUpDragableAnnotations(
        this.annotationGroupElsById,
        updateAnnotationByIdAndAttributes
      );
    }

    this.positionAnnotationLinksAndPointers();

    paintLinksAndPointers(
      this.$annotationLinks,
      this.$annotationPointers,
      this.annotationDataById
    );
  }

  getAnnotationColor(annotation) {
    const annotationGroupEl = document.querySelector(
      `g[id="annotation-${annotation.get('id')}"]`
    );

    if (!annotationGroupEl) {
      return null;
    }

    const backgroundRectEl = annotationGroupEl.querySelector('rect.background');

    if (!backgroundRectEl) {
      return null;
    }

    return backgroundRectEl.getAttribute('fill');
  }

  redrawSingleAnnotationColor(annotation, newColor) {
    if (isNullOrFalse(annotation.getIn(['style', 'colorfulBackground']))) {
      return;
    }

    const annotationGroupEl = this.annotationGroupElsById.get(
      annotation.get('id')
    );

    if (annotationGroupEl) {
      const backgroundRectEl =
        annotationGroupEl.querySelector('rect.background');

      if (backgroundRectEl) {
        backgroundRectEl.setAttribute('fill', newColor);
      }

      const orderIndicatorBackgroundCircleEl = annotationGroupEl.querySelector(
        'circle.index-background'
      );

      if (orderIndicatorBackgroundCircleEl) {
        orderIndicatorBackgroundCircleEl.setAttribute('fill', newColor);
      }
    } else {
      console.warn(
        `Annotation group element for annotation ${annotation.get(
          'id'
        )} not found.`
      );
    }

    const annotationLinkEl = document.querySelector(
      `path[id="annotation-link-${annotation.get('id')}"]`
    );

    if (annotationLinkEl) {
      annotationLinkEl.setAttribute('stroke', newColor);
    } else {
      console.warn(
        `Annotation link element for annotation ${annotation.get(
          'id'
        )} not found.`
      );
    }

    this.repaintAnnotationLinkAndPointer(
      annotation.get('id'),
      annotation.setIn(['style', 'color'], newColor).toJS()
    );
  }

  redrawSingleAnnotation(
    annotationId,
    newText,
    annotationData: {
      date?: string;
      style: {
        color?: string;
        colorfulBackground?: boolean;
        fontSize?: number;
        length?: number;
        padding: {
          horizontal: number;
          vertical: number;
        };
        showTimestamp?: boolean;
        textAlign?: string;
        textColor?: string;
      };
    }
  ) {
    const annotationGroupEl = this.annotationGroupElsById.get(annotationId);

    if (!annotationGroupEl) {
      console.warn(`Couldn't find "g" for annotation with ID=${annotationId}`);
      return;
    }

    const textEl = annotationGroupEl.querySelector('text.annotation-title');

    if (!textEl) {
      console.warn(
        `Couldn't find "g > text.annotation-title" for annotation with ID=${annotationId}`
      );
      return;
    }

    const backgroundRectEl = annotationGroupEl.querySelector('rect.background');
    const foregroundRectEl = annotationGroupEl.querySelector('rect.foreground');
    const indexBackgroundCircle = annotationGroupEl.querySelector(
      'circle.index-background'
    );
    const orderIndicatorTextEl = annotationGroupEl.querySelector('text.index');
    const timestampTextEl = annotationGroupEl.querySelector('text.timestamp');

    // NOTE: `setHTML` doesn't work for some reason.
    textEl.innerHTML = breakAnnotationTextIntoMultipleLines(
      annotationData.style.length,
      newText
    );

    textEl.setAttribute('fill', annotationData.style.textColor);

    const annotationIdx = orderIndicatorTextEl.textContent || '?';

    renderAnnotation(
      this.story.get('id'),
      this.story.getIn(['annotationSet', 'style']).toJS(),
      annotationData,
      annotationIdx,
      annotationGroupEl,
      textEl,
      backgroundRectEl,
      foregroundRectEl,
      indexBackgroundCircle,
      orderIndicatorTextEl,
      timestampTextEl
    );

    this.repaintAnnotationLinkAndPointer(annotationId, annotationData);
  }

  repaintAnnotationLinkAndPointer(annotationId, annotationData) {
    paintLinksAndPointers(
      this.$annotationLinks.filter(
        (annotation) => annotation.id === annotationId
      ),
      this.$annotationPointers.filter(
        (d) => d.datum.annotations[0].id === annotationId
      ),
      new Map([[annotationId, annotationData]])
    );
  }

  automaticallyRearrangeAnnotations(saveAllAnnotationsPositions) {
    // FIXME: some events might not be displayed, for example if there are
    // multiple events in the same date bucket. Get the number from elements in
    // the UI instead.
    const numberOfAnnotations = this.annotationBBoxesById.size;

    const numberOfAnnotationColumns = numberOfAnnotations;
    const availablePerAnnotationWidth =
      this.chartAreaBBox.width / numberOfAnnotationColumns;

    // Establish columns.
    const columnCenters = new Array(numberOfAnnotationColumns);
    let columnCurrentOffset = 0;

    for (let i = 0; i < numberOfAnnotationColumns; i += 1) {
      columnCenters[i] =
        (2 * columnCurrentOffset + availablePerAnnotationWidth) / 2;
      columnCurrentOffset += availablePerAnnotationWidth;
    }

    // Build a mapping from event mark index (i) to event mark cell. The mapping
    // is via a array indexing.
    const annotationPointerCellsByAnnotationId = new Map(
      Array.from(
        this.annotationPointerCoordinatesByAnnotationId,
        ([annotationId, [annotationPointerCellX, annotationPointerCellY]]) => [
          annotationId,
          [
            coordinateXToCellX(
              this.chartAreaBBox.width,
              numberOfAnnotationColumns,
              annotationPointerCellX
            ),
            coordinateYToCellY(
              this.chartAreaBBox.height,
              NUMBER_OF_ANNOTATION_ROWS,
              annotationPointerCellY
            ),
          ],
        ]
      )
    );

    const recommendedYPlacementsByAnnotationId = recommendAnnotationYPlacements(
      this.chartAreaBBox,
      annotationPointerCellsByAnnotationId,
      numberOfAnnotationColumns
    );

    const annotationBBoxes = Array.from(this.annotationBBoxesById.values());

    d3.forceSimulation(annotationBBoxes)
      .alpha(0.4)
      .alphaDecay(0.1)
      .velocityDecay(0.4)
      .force(
        'cellX',
        d3.forceX((node) => columnCenters[node.index]).strength(0.8)
      )
      .force(
        'annotationPointerX',
        d3
          .forceX(
            (annotationBBox) =>
              this.annotationPointerCoordinatesByAnnotationId.get(
                annotationBBox.annotationId
              )[0]
          )
          .strength(0.8)
      )
      .force(
        'cellY',
        d3
          .forceY((annotationBBox) => {
            const recommendation = recommendedYPlacementsByAnnotationId.get(
              annotationBBox.annotationId
            );

            if (!recommendation) {
              return;
            }

            const { recommendedYCoordinate } = recommendation;

            return recommendedYCoordinate - annotationBBox.height / 4;
          })
          .strength(0.5)
      )
      .force(
        'annotationPointerY',
        d3
          .forceY(
            (annotationBBox) =>
              this.annotationPointerCoordinatesByAnnotationId.get(
                annotationBBox.annotationId
              )[1]
          )
          .strength(0.5)
      )
      .force(
        'collision',
        d3BboxCollide
          .bboxCollide((annotationBBox) => {
            const padding = { horizontal: 5, vertical: 5 };

            return [
              [
                annotationBBox.x - padding.horizontal,
                annotationBBox.y - padding.vertical,
              ],
              [
                annotationBBox.x +
                  annotationBBox.width +
                  2 * padding.horizontal,
                annotationBBox.y + annotationBBox.height + 2 * padding.vertical,
              ],
            ];
          })
          .strength(1)
          .iterations(1)
      )
      .on('tick', this.positionAnnotationLinksAndPointers)
      .on('end', () => saveAllAnnotationsPositions(this.annotationBBoxesById));
  }

  setUpDragableAnnotations(
    annotationGroupElsById,
    updateAnnotationByIdAndAttributes
  ) {
    setUpDragableElements({
      svgEl: this._svgEl,
      elementsById: annotationGroupElsById,
      elementBBoxesById: this.annotationBBoxesById,
      onElementDragStart: () => {
        isFunction(this.onAnnotationDragStart) && this.onAnnotationDragStart();
      },
      onElementDrag: (_annotationId, [_x, _y]) => {
        this.positionAnnotationLinksAndPointers();
      },
      onElementDragEnd: (annotationId, positionAtDragEnd) => {
        updateAnnotationByIdAndAttributes(annotationId, {
          style_position: positionAtDragEnd,
        });

        isFunction(this.onAnnotationDragEnd) &&
          this.onAnnotationDragEnd(annotationId, positionAtDragEnd);
      },
    });
  }
}
